import { Button, Field, Input, Radio, RadioGroup } from "@fluentui/react-components";
import { UploaderFile } from "#root/components/ImageUploader";
import ImageUploader from "#root/components/ImageUploader";
import "./styles.scss";
import { useNavigate } from "react-router-dom";
import { ROUTES } from "#root/routes/constants";
import { useState } from "react";
import { TENANT_TYPE } from "#root/utils/tenants";

export type ApiSettingKey = "metroPcrVictims" | "metroPcrContacts" | "webEocVictims";

export interface ApiSettings {
    metroPcrVictims: boolean;
    metroPcrContacts: boolean;
    webEocVictims: boolean;
}

export const API_SETTING_LABELS: Record<ApiSettingKey, string> = {
    metroPcrVictims: "MetroPCR Victims",
    metroPcrContacts: "MetroPCR Contacts",
    webEocVictims: "WebEOC Victims"
};

export const DEFAULT_API_SETTINGS: ApiSettings = {
    metroPcrVictims: false,
    metroPcrContacts: false,
    webEocVictims: false
};

export interface OrganizationFormData {
    organizationName: string;
    organizationId: string;
    organizationLogo?: UploaderFile;
    organizationType: string;
    apiSettings: ApiSettings;
}

interface OrganizationFormProps {
    initialData?: OrganizationFormData;
    onSubmit: (data: OrganizationFormData) => Promise<void>;
    submitButtonText: string;
    isSubmitting: boolean;
    editMode?: boolean;
    onApiSettingUncheckBlocked?: (key: ApiSettingKey) => boolean;
}

const OrganizationForm: React.FC<OrganizationFormProps> = ({
    initialData,
    onSubmit,
    submitButtonText,
    isSubmitting,
    editMode = false,
    onApiSettingUncheckBlocked
}) => {
    const navigate = useNavigate();
    const [logo, setLogo] = useState<UploaderFile | undefined>(initialData?.organizationLogo);
    const [name, setName] = useState(initialData?.organizationName || "");
    const [id, setId] = useState(initialData?.organizationId || "");
    const [type, setType] = useState(initialData?.organizationType || TENANT_TYPE.RAPID_REUNIFICATION);
    const [apiSettings, setApiSettings] = useState<ApiSettings>(
        initialData?.apiSettings ?? DEFAULT_API_SETTINGS
    );

    const handleApiSettingChange = (key: ApiSettingKey, checked: boolean) => {
        if (!checked && apiSettings[key] && onApiSettingUncheckBlocked?.(key)) {
            return;
        }
        setApiSettings(prev => ({ ...prev, [key]: checked }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        await onSubmit({
            organizationName: name,
            organizationId: id,
            organizationLogo: logo,
            organizationType: type,
            apiSettings
        });
    };

    return (
        <form className="form-step" onSubmit={handleSubmit}>
            <h3>Organization Information</h3>
            <div className="field-two-columns">
                <Field label="Organization Name" required>
                    <Input
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                    />
                </Field>
                <Field label="Organization ID" required>
                    <Input
                        value={id}
                        onChange={(e) => setId(e.target.value)}
                    />
                </Field>
            </div>

            <Field label="App Settings" required>
                <RadioGroup layout="horizontal" required
                    disabled={editMode}
                    value={type}
                    onChange={(_, data) => {
                        setType(data.value);
                        if (data.value === TENANT_TYPE.MERRGE_211) {
                            setApiSettings(DEFAULT_API_SETTINGS);
                        }
                    }}>
                    <Radio value={TENANT_TYPE.RAPID_REUNIFICATION} label="Rapid Reunification" />
                    <Radio value={TENANT_TYPE.MERRGE_211} label="MERRGE™ 211" />
                </RadioGroup>
            </Field>

            {type !== TENANT_TYPE.MERRGE_211 && (
                <Field label="API Settings">
                    <div className="api-settings">
                        {(Object.keys(API_SETTING_LABELS) as ApiSettingKey[]).map(key => (
                            <Button
                                key={key}
                                type="button"
                                appearance={apiSettings[key] ? "primary" : "secondary"}
                                onClick={() => handleApiSettingChange(key, !apiSettings[key])}
                            >
                                {API_SETTING_LABELS[key]}
                            </Button>
                        ))}
                    </div>
                </Field>
            )}

            <Field label="Organization Logo">
                <ImageUploader
                    maxFiles={1}
                    files={logo ? [logo] : []}
                    onChange={(files) => setLogo(files[0])}
                />
            </Field>

            <div className="form-actions">
                <Button
                    type="submit"
                    appearance="primary"
                    size="large"
                    disabled={isSubmitting}
                >
                    {isSubmitting ? 'Loading...' : submitButtonText}
                </Button>
                <Button appearance="secondary" size="large" onClick={() => navigate(ROUTES.ROOT)}>
                    Cancel
                </Button>
            </div>
        </form>
    );
};

export default OrganizationForm;
